<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { PokerChipIcon } from '@uiKit/icons'

const props = defineProps<{
  totalTokens?: number
  propertiesCount?: number
  walletAddress?: string
}>()

const COPIED_FEEDBACK_DURATION_MS = 1500
const ADDRESS_SHORT_THRESHOLD = 16
const ADDRESS_PREFIX_LENGTH = 10
const ADDRESS_SUFFIX_LENGTH = 8

const copied = ref(false)

function copyAddress() {
  if (!props.walletAddress) return
  navigator.clipboard.writeText(props.walletAddress).then(() => {
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, COPIED_FEEDBACK_DURATION_MS)
  })
}

function shortAddress(addr = '') {
  if (!addr || addr.length <= ADDRESS_SHORT_THRESHOLD) return addr
  return `${addr.slice(0, ADDRESS_PREFIX_LENGTH)}...${addr.slice(-ADDRESS_SUFFIX_LENGTH)}`
}
</script>

<template>
  <Box direction="col" gap="4" class="tbc">
    <Box align="center" justify="between">
      <Text type="body" weight="medium" transform="uppercase" color="muted">
        TOTAL ARVA TOKENS HELD
      </Text>
      <Box align="center" gap="1" class="tbc__badge">
        <Box class="tbc__badge-dot" />
        <Text type="caption" transform="uppercase" class="tbc__badge-text">Token Enabled</Text>
      </Box>
    </Box>

    <Box align="center" gap="1">
      <Text is="span" type="display-lg" class="tbc__amount">
        {{ totalTokens ?? 0 }} ARVA
      </Text>
      <PokerChipIcon class="tbc__chip-icon" color-from="#88d66c" color-to="#447d0e" />
    </Box>

    <Box align="center" gap="1" class="tbc__sub-row">
      <Box class="tbc__dot" />
      <Text type="body" weight="medium" color="muted">
        Across {{ propertiesCount ?? 0 }} {{ propertiesCount === 1 ? 'property' : 'properties' }}
      </Text>
    </Box>

    <Box
      is="button"
      align="center"
      justify="between"
      gap="3"
      class="tbc__address-bar"
      :title="copied ? 'Copied!' : 'Copy address'"
      type="button"
      aria-label="Copy wallet address"
      @click="copyAddress"
    >
      <Text type="body-sm" color="muted" truncate class="tbc__address-text">
        {{ shortAddress(walletAddress) }}
      </Text>
      <PiIcon :name="copied ? 'check' : 'copy'" class="tbc__copy-icon" />
    </Box>
  </Box>
</template>

<style scoped>
.tbc {
  flex: 1;
  background: #f5fee7;
  border: 1px solid #a9f250;
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
  overflow: hidden;
  position: relative;
}

.tbc::before {
  content: '';
  position: absolute;
  bottom: -28px;
  left: -22px;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(88, 164, 12, 0.15);
  filter: blur(12px);
  pointer-events: none;
}

.tbc__badge {
  display: inline-flex;
  background: #447d0e;
  border-radius: 26px;
  padding: 3px 10px;
}

.tbc__badge-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #f5fee7;
  flex-shrink: 0;
}

.tbc__badge-text {
  color: #f5fee7 !important;
  line-height: 15px !important;
}

.tbc__amount {
  font-family: 'Playfair Display', serif !important;
  color: #447d0e !important;
  letter-spacing: -0.64px !important;
  line-height: 1.15 !important;
}

.tbc__chip-icon {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}

.tbc__sub-row {
  margin-top: -8px;
}

.tbc__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #58a40c;
  flex-shrink: 0;
}

.tbc__address-bar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-radius: var(--p-border-radius-lg);
  padding: 0.625rem 1rem;
  border: none;
  cursor: pointer;
  width: 100%;
}

.tbc__address-bar:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.tbc__address-text {
  min-width: 0;
}

.tbc__copy-icon {
  flex-shrink: 0;
  font-size: 18px;
  color: var(--p-text-muted-color);
}

@media (max-width: 992px) {
  .tbc {
    padding: 1rem;
  }

  .tbc__amount {
    font-size: 26px !important;
  }

  .tbc__address-bar {
    padding: 0.5rem 0.75rem;
  }

  .tbc__address-text {
    font-size: 11px !important;
  }
}
</style>
